import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../services/api";

function Create() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    project: "",
    paid_by: "",
    amount: "",
    note: "",
    currency: "AFN", // Default to AFN
    exchange_rate: 1,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Currency symbol function
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋',
      'EUR': '€'
    };
    return symbols[currencyCode] || currencyCode;
  };

  // Fetch projects and employees
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, empRes] = await Promise.all([
          api.get("projects/"),
          api.get("employees/"),
        ]);
        setProjects(projRes.data);
        setEmployees(empRes.data);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load projects or employees.");
      }
    };
    fetchData();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Auto-set exchange rate to 1 if AFN is selected
    if (name === 'currency' && value === 'AFN') {
      setFormData(prev => ({
        ...prev,
        exchange_rate: 1
      }));
    }
  };

  // Create Payment
  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!formData.project || !formData.amount || !formData.currency) {
      setError("Project, Amount, and Currency are required.");
      setSubmitting(false);
      return;
    }

    if (formData.currency !== 'AFN' && (!formData.exchange_rate || formData.exchange_rate <= 0)) {
      setError("Exchange rate is required for non-AFN currencies.");
      setSubmitting(false);
      return;
    }

    try {
      await api.post("payments/", {
        project: parseInt(formData.project),
        paid_by: formData.paid_by ? parseInt(formData.paid_by) : null,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        exchange_rate: parseFloat(formData.exchange_rate),
        note: formData.note || "",
      });
      navigate("/payments");
    } catch (err) {
      console.error("Payment creation error:", err);
      if (err.response && err.response.data) {
        const serverError =
          err.response.data.amount || err.response.data.detail || "Failed to add payment.";
        setError(serverError.toString());
      } else {
        setError("Network or server error. Please check your connection.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <Link
          to="/payments"
          className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Add Payment
          </h1>
          <p className="text-gray-600 mt-2">Record a new payment transaction</p>
        </div>
      </div>

      {/* Form */}
      <div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleCreate} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project *</label>
              <select
                name="project"
                value={formData.project}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                required
              >
                <option value="">Select Project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Paid By (Employee)</label>
              <select
                name="paid_by"
                value={formData.paid_by}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              >
                <option value="">Select Employee</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency *</label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                required
              >
                <option value="AFN">Afghan Afghani (AFN)</option>
                <option value="USD">US Dollar (USD)</option>
                <option value="EUR">Euro (EUR)</option>
              </select>
            </div>

            {/* Exchange Rate Field - Only show if currency is NOT AFN */}
            {formData.currency && formData.currency !== 'AFN' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">1 {formData.currency} =</span>
                  <input
                    type="number"
                    name="exchange_rate"
                    value={formData.exchange_rate}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl p-3 pl-20 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    placeholder="0.00"
                    step="0.000001"
                    min="0.000001"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">AFN</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{getCurrencySymbol(formData.currency)}</span>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  className="w-full border border-gray-300 rounded-xl p-3 pl-8 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                placeholder="Additional notes about this payment..."
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <Link
              to="/payments"
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <span>+</span>
                  <span>Add Payment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Create;